import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { RefreshCw, CheckCircle, AlertTriangle, Key, Globe, Shield, Save, Database } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';

interface WebConSettings {
  apiUrl: string;
  clientId: string;
  apiKey: string;
}

interface WebConStatus {
  connected: boolean;
  apiVersions: { version: string; url: string }[];
}

export function WebConPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ apiUrl: '', clientId: '', apiKey: '' });

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings', 'webcon'],
    queryFn: () => api.get<WebConSettings>('/settings/webcon'),
  });
  const settings = settingsData?.data;

  const { data: statusData, isLoading: statusLoading, refetch, isFetching } = useQuery({
    queryKey: ['settings', 'webcon', 'status'],
    queryFn: () => api.get<WebConStatus>('/settings/webcon/status'),
    refetchOnWindowFocus: false,
  });
  const status = statusData?.data;

  const saveMutation = useMutation({
    mutationFn: (data: Partial<WebConSettings>) => api.put('/settings/webcon', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'webcon'] });
      setEditing(false);
      toast('success', 'Setările WebCon au fost salvate cu succes');
    },
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.post('/settings/webcon/refresh'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e) => toast('error', `Eroare la reîmprospătare: ${e.message}`),
  });

  const isLoading = settingsLoading || statusLoading;

  const startEdit = () => {
    setForm({
      apiUrl: settings?.apiUrl ?? '',
      clientId: settings?.clientId ?? '',
      apiKey: '',
    });
    setEditing(true);
  };

  const handleSave = () => {
    const payload: Record<string, string> = {};
    if (form.apiUrl && form.apiUrl !== settings?.apiUrl) payload.apiUrl = form.apiUrl;
    if (form.clientId && form.clientId !== settings?.clientId) payload.clientId = form.clientId;
    if (form.apiKey) payload.apiKey = form.apiKey;
    if (Object.keys(payload).length > 0) saveMutation.mutate(payload);
    else setEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">WebCon BPS</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
          >
            <Database size={16} className={refreshMutation.isPending ? 'animate-spin' : ''} />
            {refreshMutation.isPending ? 'Se reîmprospătează...' : 'Reîmprospătează date'}
          </Button>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> Verifică conexiunea
          </Button>
        </div>
      </div>

      {refreshMutation.isSuccess && (
        <div className="rounded-md bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
          Cache-ul WebCon a fost invalidat. Datele vor fi reîncărcate la următoarea cerere.
        </div>
      )}

      {/* Connection Status */}
      <Card>
        <CardHeader><h2 className="font-semibold">Status conexiune</h2></CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-8 w-48" /> : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {status?.connected ? (
                  <>
                    <CheckCircle className="text-green-500" size={24} />
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">Conectat</Badge>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="text-red-500" size={24} />
                    <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">Deconectat</Badge>
                  </>
                )}
              </div>
              {status?.apiVersions && status.apiVersions.length > 0 && (
                <div className="text-sm text-gray-500">
                  Versiuni API disponibile: {status.apiVersions.map((v) => v.version).join(', ')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credentials */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2"><Shield size={18} /> Credențiale WebCon</h2>
            {!editing && (
              <Button variant="outline" size="sm" onClick={startEdit}>Editează</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {settingsLoading ? <Skeleton className="h-24 w-full" /> : editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">API URL</label>
                <input
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.apiUrl}
                  onChange={(e) => setForm((f) => ({ ...f, apiUrl: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Client ID</label>
                <input
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.clientId}
                  onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">API Key (lasă gol pentru a păstra cel existent)</label>
                <input
                  type="password"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.apiKey}
                  onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                  placeholder="••••••••"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  <Save size={16} /> Salvează
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Anulează</Button>
              </div>
              {saveMutation.isError && (
                <p className="text-sm text-red-600">Eroare la salvare: {(saveMutation.error as Error).message}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Globe size={18} className="mt-0.5 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">API URL</p>
                  <p className="text-sm font-mono text-gray-900">{settings?.apiUrl ?? '—'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Key size={18} className="mt-0.5 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Client ID</p>
                  <p className="text-sm font-mono text-gray-900">{settings?.clientId ?? '—'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Key size={18} className="mt-0.5 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">API Key</p>
                  <p className="text-sm font-mono text-gray-900">{settings?.apiKey ?? '—'}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports info */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader><h2 className="text-sm font-semibold">Raport Clienți (#323)</h2></CardHeader>
          <CardContent className="text-sm text-gray-500">
            Clienții sunt preluați în timp real din WebCon BPS.
            Datele NU sunt stocate local în baza de date.
          </CardContent>
        </Card>
        <Card>
          <CardHeader><h2 className="text-sm font-semibold">Raport Contracte (#324)</h2></CardHeader>
          <CardContent className="text-sm text-gray-500">
            Contractele sunt preluate în timp real.
            Include tipul, perioada, orele rămase.
          </CardContent>
        </Card>
        <Card>
          <CardHeader><h2 className="text-sm font-semibold">Raport Proiecte (#325)</h2></CardHeader>
          <CardContent className="text-sm text-gray-500">
            Proiectele sunt preluate în timp real.
            Legate de un client specific.
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><h2 className="font-semibold">Informații importante</h2></CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <p>• Datele WebCon sunt interogate în timp real la fiecare încărcare de pagină.</p>
          <p>• Cache-ul intern se invalidează la fiecare 30 de minute, sau manual cu butonul &quot;Reîmprospătează date&quot;.</p>
          <p>• Versiunea API se detectează automat prin apelul <code>/api/data</code>.</p>
          <p>• ID-urile WebCon (elementId) sunt folosite ca referință în tichetele locale.</p>
        </CardContent>
      </Card>
    </div>
  );
}
